import React, { useState } from 'react';
import {
    ActionIcon,
    Button,
    Group,
    MultiSelect,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { DataTable } from 'mantine-datatable';
import { IconPencil, IconPlus, IconTrash, IconX } from '@tabler/icons-react';

import {
    CREATE_TRAINING_CATEGORY,
    DELETE_TRAINING_CATEGORY,
    UPDATE_TRAINING_CATEGORY,
} from '../../apollo/mutations/training';
import { GET_TRAINING_CATEGORIES } from '../../apollo/queries/training';
import { getErrorMessage } from '../../helpers/getErrorMessage';
import {
    TRAINING_AUDIENCE_OPTIONS,
    TrainingAudience,
    TrainingCategory,
} from '../../types/training';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import When from '../common/When';
import { commonTableStyles } from '../../constants/tableStyles';
import { TrainingAudienceBadges } from './TrainingAudienceBadges';

type Props = {
    categories: TrainingCategory[];
};

type CategoryFormValues = {
    name: string;
    description: string;
    audiences: TrainingAudience[];
};

export function TrainingCategoryManager({ categories }: Props) {
    const [editingCategory, setEditingCategory] =
        useState<TrainingCategory | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<TrainingCategory | null>(
        null,
    );

    const form = useForm<CategoryFormValues>({
        initialValues: {
            name: '',
            description: '',
            audiences: ['GENERAL'],
        },
        validate: {
            name: value =>
                value.trim().length === 0 ? 'Category name is required' : null,
            audiences: value =>
                value.length === 0 ? 'Select at least one audience' : null,
        },
    });

    const [createCategory, { loading: creating }] = useMutation(
        CREATE_TRAINING_CATEGORY,
        {
            refetchQueries: [{ query: GET_TRAINING_CATEGORIES }],
        },
    );

    const [updateCategory, { loading: updating }] = useMutation(
        UPDATE_TRAINING_CATEGORY,
        {
            refetchQueries: [{ query: GET_TRAINING_CATEGORIES }],
        },
    );

    const [deleteCategory, { loading: deleting }] = useMutation(
        DELETE_TRAINING_CATEGORY,
        {
            refetchQueries: [{ query: GET_TRAINING_CATEGORIES }],
        },
    );

    const resetForm = () => {
        setEditingCategory(null);
        form.reset();
    };

    const startEdit = (category: TrainingCategory) => {
        setEditingCategory(category);
        form.setValues({
            name: category.name,
            description: category.description ?? '',
            audiences: category.audiences?.length
                ? [...category.audiences]
                : ['GENERAL'],
        });
    };

    const handleEditClick = (category: TrainingCategory) => {
        startEdit(category);
    };

    const handleDeleteClick = (category: TrainingCategory) => {
        setDeleteTarget(category);
    };

    const handleCloseDelete = () => {
        setDeleteTarget(null);
    };

    const handleSubmit = async (values: CategoryFormValues) => {
        const name = values.name.trim();
        const description = values.description.trim() || null;

        try {
            if (editingCategory) {
                await updateCategory({
                    variables: {
                        input: {
                            id: editingCategory.id,
                            name,
                            description,
                            audiences: values.audiences,
                        },
                    },
                });
                notifications.show({
                    title: 'Category updated',
                    message: `"${name}" was saved`,
                    color: 'teal',
                });
            } else {
                await createCategory({
                    variables: {
                        input: {
                            name,
                            description,
                            audiences: values.audiences,
                        },
                    },
                });
                notifications.show({
                    title: 'Category created',
                    message: `"${name}" is ready for materials`,
                    color: 'teal',
                });
            }
            resetForm();
        } catch (error) {
            notifications.show({
                title: editingCategory
                    ? 'Failed to update category'
                    : 'Failed to create category',
                message: getErrorMessage(error),
                color: 'red',
            });
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) {
            return;
        }
        try {
            await deleteCategory({ variables: { id: deleteTarget.id } });
            notifications.show({
                title: 'Category deleted',
                message: `"${deleteTarget.name}" and its materials were removed`,
                color: 'teal',
            });
            if (editingCategory?.id === deleteTarget.id) {
                resetForm();
            }
            setDeleteTarget(null);
        } catch (error) {
            notifications.show({
                title: 'Failed to delete category',
                message: getErrorMessage(error),
                color: 'red',
            });
        }
    };

    const isSaving = creating || updating;

    return (
        <>
            <p className="text-sm text-gray-500 mb-4">
                Audience controls who can see materials in a category. Users see
                General plus their own role. Admins see everything.
            </p>

            <form onSubmit={form.onSubmit(handleSubmit)} className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <TextInput
                        label={
                            <When
                                condition={Boolean(editingCategory)}
                                fallback="New category"
                            >
                                Edit category
                            </When>
                        }
                        placeholder="e.g. Price Surveys for Store Managers"
                        {...form.getInputProps('name')}
                    />
                    <TextInput
                        label="Description"
                        placeholder="Optional"
                        {...form.getInputProps('description')}
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
                    <MultiSelect
                        label="Audience"
                        placeholder="Who can see this category?"
                        data={TRAINING_AUDIENCE_OPTIONS}
                        searchable
                        {...form.getInputProps('audiences')}
                    />
                    <Group gap="xs" className="w-fit shrink-0">
                        <When condition={Boolean(editingCategory)}>
                            <Button
                                type="button"
                                size="sm"
                                variant="default"
                                leftSection={<IconX size={14} />}
                                onClick={resetForm}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                        </When>
                        <Button
                            type="submit"
                            size="sm"
                            leftSection={
                                <When
                                    condition={Boolean(editingCategory)}
                                    fallback={<IconPlus size={14} />}
                                >
                                    <IconPencil size={14} />
                                </When>
                            }
                            loading={isSaving}
                        >
                            <When
                                condition={Boolean(editingCategory)}
                                fallback="Add category"
                            >
                                Save changes
                            </When>
                        </Button>
                    </Group>
                </div>
            </form>

            <Text size="sm" fw={600} className="mb-2">
                Existing categories
            </Text>
            <DataTable<TrainingCategory>
                withTableBorder
                borderRadius="md"
                striped
                highlightOnHover
                verticalSpacing="xs"
                fz="sm"
                withColumnBorders={false}
                minHeight={160}
                records={categories}
                idAccessor="id"
                noRecordsText="No categories yet. Create one, then switch to Upload material."
                rowClassName={() => 'bg-white hover:bg-gray-50'}
                styles={commonTableStyles}
                columns={[
                    {
                        accessor: 'name',
                        title: 'Name',
                    },
                    {
                        accessor: 'description',
                        title: 'Description',
                        render: row => row.description || '-',
                    },
                    {
                        accessor: 'audiences',
                        title: 'Audience',
                        render: row => (
                            <TrainingAudienceBadges audiences={row.audiences} />
                        ),
                    },
                    {
                        accessor: 'actions',
                        title: 'Actions',
                        width: 100,
                        render: row => (
                            <Group gap={4} wrap="nowrap">
                                <Tooltip label="Edit category">
                                    <ActionIcon
                                        variant="subtle"
                                        color="blue"
                                        size="sm"
                                        onClick={() => handleEditClick(row)}
                                        aria-label={`Edit ${row.name}`}
                                    >
                                        <IconPencil size={16} />
                                    </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Delete category">
                                    <ActionIcon
                                        variant="subtle"
                                        color="red"
                                        size="sm"
                                        onClick={() => handleDeleteClick(row)}
                                        aria-label={`Delete ${row.name}`}
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        ),
                    },
                ]}
            />

            <ConfirmDeleteModal
                opened={Boolean(deleteTarget)}
                close={handleCloseDelete}
                onConfirm={handleDelete}
                loading={deleting}
                subject={deleteTarget?.name}
                message={`Delete "${deleteTarget?.name}" and all materials in it? This cannot be undone.`}
            />
        </>
    );
}
